import React, { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { useSelector } from 'react-redux';
import ApolloClient from 'apollo-client';
import { NormalizedCacheObject } from 'apollo-cache-inmemory';
import { MockApolloClient } from 'mock-apollo-client';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { DeprecatedButton, Checkbox } from '@modernhealth/vital-components';

import { useCreateEventMutation } from '../../documents/CreateEventMutation.generated';
import { getCurrentUser } from '../../../../../selectors/userSelectors';
import {
  ClientSummaryNode,
  CreateEventResult,
  ExpectedError,
} from '../../../../../generated/webapp_gql';
import LoadingProgress from '../../ScheduleSessionForm/LoadingProgress/LoadingProgress';
import {
  canScheduleClient,
  convertStrToDateWithLocalTimezone,
  getDisplayErrorMessage,
  getSessionDurationInMinutes,
} from '../../ScheduleSessionForm/helpers';
import {
  ProviderAvailabilityQuery,
  SessionDetails,
  PROVIDER_AVAILABILITY_QUERY,
} from '../../ScheduleSessionForm/constants';
import { ClientSelectDocument } from '../../documents/ClientSelectQuery.generated';
import MemberSelector from '../MemberSelector';
import SessionDatePicker from '../../ScheduleSessionForm/SessionDatePicker/SessionDatePicker';
import SessionTimePicker from '../../ScheduleSessionForm/SessionTimePicker/SessionTimePicker';
import ScheduledDurationDisplay from '../../ScheduleSessionForm/ScheduledDurationDisplay/ScheduledDurationDisplay';
export interface Props {
  memberClient: ApolloClient<NormalizedCacheObject> | MockApolloClient;
  onCancel: () => void;
  onSuccess: (sessionDetails: SessionDetails) => void;
}

const isCreateEventResult = (
  result: CreateEventResult | ExpectedError,
): result is CreateEventResult => {
  return (result as CreateEventResult).uuid !== undefined;
};
const isExpectedError = (result: CreateEventResult | ExpectedError): result is ExpectedError => {
  return (result as ExpectedError).message !== undefined;
};
const SchedulerForm: React.FC<Props> = ({ memberClient, onCancel, onSuccess }) => {
  const currentUser = useSelector(getCurrentUser);
  const defaultSessionDuration = currentUser.isTherapist ? 60 : 30;
  const providerIsPsychiatrist = currentUser.isPsychiatrist;
  const [selectedClient, setSelectedClient] = useState<ClientSummaryNode | undefined>();
  const [selectedDatetime, setSelectedDatetime] = useState<Date>();
  const [sessionDuration, setSessionDuration] = useState(defaultSessionDuration);
  const [errorMessage, setErrorMessage] = useState('');
  const [ignoreWorkingHours, setIgnoreWorkingHours] = useState(false);
  const { data, loading } = useQuery<ProviderAvailabilityQuery>(PROVIDER_AVAILABILITY_QUERY, {
    client: memberClient,
    variables: {
      providerUuid: currentUser.uuid,
      ignoreWorkingHours,
      memberUuid: selectedClient?.memberUuid,
    },
    fetchPolicy: 'network-only',
  });
  const selectableDates =
    data?.providerAvailabilityForProvider?.map((availability) =>
      convertStrToDateWithLocalTimezone(availability.start),
    ) || [];
  const selectableDatesIso = selectableDates.map((date) => date.toISOString());
  // Given a time, return if the timeslot is available on the currently selected date
  const checkTimeAvailability = useCallback(
    (pickerTime: Date): boolean => {
      if (!data?.providerAvailabilityForProvider || !selectedDatetime) {
        return false;
      }
      // Proposed time is given as a Date object but the defaults to current day. Set it to the selected date
      const proposedTime = new Date(selectedDatetime);
      proposedTime.setHours(pickerTime.getHours());
      proposedTime.setMinutes(pickerTime.getMinutes());
      proposedTime.setSeconds(0);
      proposedTime.setMilliseconds(0);
      return selectableDatesIso.includes(proposedTime.toISOString());
    },
    [data, selectableDatesIso, selectedDatetime],
  );
  const flags = useFlags();
  const isFormValid = canScheduleClient({
    currentUser,
    selectedClient,
    selectedDatetime,
    showHpiClientStatusInfo: flags.showHpiClientStatusInfo,
  });
  const [
    createEventMutation,
    { data: createEventMutationData, loading: isCreateEventMutationLoading },
  ] = useCreateEventMutation({
    client: memberClient,
    refetchQueries: [{ query: ClientSelectDocument }], // async invalidate client select cache to update cached member eligibility status
  });
  let endDatetime;
  if (selectedDatetime) {
    endDatetime = new Date(selectedDatetime);
    endDatetime.setMinutes(endDatetime.getMinutes() + sessionDuration);
  }

  useEffect(() => {
    // make sure that selected time-slot is available
    // when transitioning from `ignoreWorkingHours` mode to a regular one
    if (!loading && !ignoreWorkingHours && selectedDatetime) {
      const isTimeAvailable = checkTimeAvailability(selectedDatetime);
      if (!isTimeAvailable) {
        setSelectedDatetime(undefined);
      }
    }
  }, [checkTimeAvailability, ignoreWorkingHours, loading, selectedDatetime, setSelectedDatetime]);
  useEffect(() => {
    if (data?.providerAvailabilityForProvider?.length) {
      setSessionDuration(
        getSessionDurationInMinutes(data.providerAvailabilityForProvider[0]) ||
          defaultSessionDuration,
      );
    }
  }, [data, defaultSessionDuration, setSessionDuration]);
  useEffect(() => {
    if (
      !isFormValid ||
      !selectedClient ||
      !selectedDatetime ||
      !endDatetime ||
      isCreateEventMutationLoading
    ) {
      return;
    }
    if (createEventMutationData?.createEvent) {
      const { formattedResponse, ok } = createEventMutationData.createEvent;
      if (formattedResponse) {
        if (ok && isCreateEventResult(formattedResponse)) {
          onSuccess({
            endTime: endDatetime,
            member: selectedClient,
            sessionState: formattedResponse.state,
            sessionUuid: formattedResponse.uuid,
            startTime: selectedDatetime,
            sessionDuration: sessionDuration,
          });
        } else if (!ok && isExpectedError(formattedResponse)) {
          setErrorMessage(
            getDisplayErrorMessage({
              currentUser,
              error: formattedResponse.message,
              selectedClient,
            }),
          );
        }
      }
    }
  }, [
    createEventMutationData,
    currentUser,
    endDatetime,
    isCreateEventMutationLoading,
    isFormValid,
    onSuccess,
    selectedClient,
    selectedDatetime,
    setErrorMessage,
  ]);
  const handleSubmit = async () => {
    if (
      !isFormValid ||
      !selectedClient ||
      !selectedDatetime ||
      !endDatetime ||
      isCreateEventMutationLoading
    ) {
      // The submit button should be disabled, but in case it isn't, do nothing.
      return;
    }
    const variables = {
      endTime: endDatetime.toISOString(),
      ignoreWorkingHours,
      memberUuid: selectedClient.memberUuid,
      startTime: selectedDatetime.toISOString(),
    };
    try {
      await createEventMutation({ variables });
    } catch (error: any) {
      if (error) {
        setErrorMessage(
          getDisplayErrorMessage({
            currentUser,
            error: error.message || 'Something went wrong! Please try again later.',
            selectedClient,
          }),
        );
      }
    }
  };
  const handleSelectDate = (selectedDate: Date) => {
    if (!data?.providerAvailabilityForProvider || loading) {
      return;
    }
    // Find the first available time for the selected date
    for (const availability of data.providerAvailabilityForProvider) {
      const availabilityStart = convertStrToDateWithLocalTimezone(availability.start);
      const availabilityEnd = convertStrToDateWithLocalTimezone(availability.end);
      if (availabilityStart.toDateString() === selectedDate.toDateString()) {
        setSelectedDatetime(availabilityStart);
        break;
      } else if (availabilityStart <= selectedDate && selectedDate < availabilityEnd) {
        // Set the selected time to midnight if the date somewhere between the date range
        setSelectedDatetime(new Date(selectedDate.toDateString()));
        break;
      }
    }
  };
  const getFormTitle = () => {
    const commonTitle = 'Schedule ';
    const psychiatrySessionTitles = {
      60: 'an initial evaluation session',
      30: 'a follow-up session',
    };
    if (providerIsPsychiatrist) return `${commonTitle}${psychiatrySessionTitles[sessionDuration]}`;
    return `${commonTitle}a session`;
  };
  const getFormSubText = () => {
    const sessionFormSubtexts = {
      0: 'You’ll be able to book clients that have available sessions left. After you book, we’ll notify them about the session through an email.',
      60: 'Initial evaluations are scheduled for 60 minutes to assess mental health symptoms and how to best treat them. After you schedule, we’ll notify your client about the session through an email.',
      30: 'Follow-ups are scheduled for 30 minutes to monitor the effect of treatment over time and make adjustments if indicated. After you schedule, we’ll notify your client about the session through an email. ',
    };
    return sessionFormSubtexts[providerIsPsychiatrist ? sessionDuration : 0];
  };
  return (
    <div className="h-full">
      <h1 className="font-black text-black text-2xl mb-4">{getFormTitle()}</h1>
      <p className="text-gray-700 text-sm leading-normal">{getFormSubText()}</p>
      <div className="rounded-md bg-gray-200 w-full mt-4 mb-8 py-6 px-2 relative">
        <div className="px-3">
          <div className="mb-2 mt-2">
            <MemberSelector
              memberClient={memberClient}
              onClientSelect={setSelectedClient}
              errorMessage={errorMessage}
            />
          </div>
          <div className="pt-6 flex">
            <div className="flex-grow w-10">
              <SessionDatePicker
                isDisabled={!selectedClient}
                onChange={handleSelectDate}
                selectedDatetime={selectedDatetime}
                selectableDates={selectableDates}
              />
            </div>
            <div className="flex-grow px-1 w-10">
              <SessionTimePicker
                filterTime={checkTimeAvailability}
                isDisabled={!selectedDatetime}
                onChange={setSelectedDatetime}
                selectedDatetime={selectedDatetime}
                timeIntervals={15}
              />
            </div>
            <div className="flex-grow-0">
              <ScheduledDurationDisplay scheduledDurationInMinutes={sessionDuration} />
            </div>
          </div>
          <div className="pt-6 flex">
            <div className="flex-grow" style={{ color: 'white' }}>
              <Checkbox
                value={ignoreWorkingHours}
                label="Show times outside of my working hours"
                onChange={setIgnoreWorkingHours}
              ></Checkbox>
            </div>
          </div>
        </div>
        {loading && <LoadingProgress />}
      </div>
      <div className="flex justify-end items-center pb-6">
        <div className="w-full mr-2">
          <DeprecatedButton
            title="Cancel"
            fullWidth
            size="large"
            variant="secondary"
            onClick={onCancel}
          />
        </div>
        <div className="w-full ml-2">
          {isCreateEventMutationLoading && <LoadingProgress />}
          <DeprecatedButton
            title="Schedule"
            fullWidth
            size="large"
            loading={isCreateEventMutationLoading}
            onClick={handleSubmit}
            disabled={!isFormValid}
          />
        </div>
      </div>
    </div>
  );
};
export default SchedulerForm;
